import type { SearchDocument } from '@/features/AudioPlayer';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type {
  MotionGraphics,
  MusicDocument,
  SfxDocument,
  VideoDocument,
} from '@music-vine/setlist';

// ============================================================================
// Typesense Document Type Guards
// ============================================================================

/**
 * Type guard to check if a search result is a motion graphics document.
 * Checks for the presence of key properties that distinguish MotionGraphics
 * from generic search results.
 *
 * @param result - The search result to check
 * @returns True if the result has motion graphics properties
 */
export function isMotionGraphicsDocument(
  result: GenericSearchResult
): result is GenericSearchResult & MotionGraphics {
  return !!(
    result &&
    typeof result === 'object' &&
    'contributor' in result &&
    'is_lut' in result &&
    'preview_video_url' in result &&
    'compatibility' in result &&
    result.contributor &&
    typeof result.contributor === 'object' &&
    'name' in result.contributor
  );
}

/**
 * Type guard to check if a search result is a stock video document.
 * Checks for the presence of key properties that distinguish VideoDocument
 * from generic search results.
 *
 * @param result - The search result to check
 * @returns True if the result has stock video properties
 */
export function isStockVideoDocument(
  result: GenericSearchResult
): result is GenericSearchResult & VideoDocument {
  return !!(
    result &&
    typeof result === 'object' &&
    'contributor_name' in result &&
    'preview_url' in result &&
    'thumbnail_url' in result &&
    'duration_seconds' in result
  );
}

/**
 * Type guard to check if a document is a MusicDocument (engagement/v3 collection).
 * Distinguishes raw Typesense music documents from basic SearchDocuments.
 *
 * @param document - The document to check
 * @returns True if the document is a MusicDocument from Typesense
 */
export function isMusicDocument(
  document: SearchDocument | MusicDocument | SfxDocument
): document is MusicDocument {
  return (
    'track_id' in document &&
    'musicvine_track_id' in document &&
    'free_decay_downloads' in document &&
    'premium_decay_downloads' in document
  );
}

/**
 * Type guard to check if a document is an SfxDocument (engagement/v3 collection).
 * Distinguishes raw Typesense SFX documents from basic SearchDocuments.
 *
 * @param document - The document to check
 * @returns True if the document is an SfxDocument from Typesense
 */
export function isSfxDocument(
  document: SearchDocument | MusicDocument | SfxDocument
): document is SfxDocument {
  return (
    'sfx_id' in document &&
    'sfx_variant_id' in document &&
    'free_decay_downloads' in document &&
    'premium_decay_downloads' in document
  );
}

/**
 * Type guard to check if a document is a basic SearchDocument.
 * Returns true if it's not a MusicDocument or SfxDocument.
 *
 * @param document - The document to check
 * @returns True if the document is a basic SearchDocument
 */
export function isBasicSearchDocument(
  document: SearchDocument | MusicDocument | SfxDocument
): document is SearchDocument {
  return !(isMusicDocument(document) || isSfxDocument(document));
}

/**
 * Helper function to normalize any document type to SearchDocument format.
 * This is used to convert engagement-based documents to the format expected by Track components.
 *
 * @param document - The document to normalize
 * @returns A normalized SearchDocument
 */
export function normalizeDocumentForTrack(
  document: SearchDocument | MusicDocument | SfxDocument
): SearchDocument {
  if (isBasicSearchDocument(document)) {
    return document;
  }

  // Convert MusicDocument or SfxDocument to SearchDocument format
  const converted = {
    ...document,
    asset_type: isMusicDocument(document) ? 'music' : 'sfx',
    ...(isSfxDocument(document) &&
      typeof document.sfx_variant_id === 'string' && {
        sfx_variant_id: Number.parseInt(document.sfx_variant_id, 10),
      }),
  };
  return converted as SearchDocument;
}
